import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { useOrderbook } from "../../hooks/useOrderbook";
import { formatPrice, formatSize } from "../../sdk/utils/display";
import { createCubicBezierEasing } from "./orderbooktool";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  StatusIndicator,
  LayoutHeader,
} from "../smartlayout/fragments";
import {
  DEFAULT_HEIGHT_ORDERBOOK,
  DEFAULT_MAX_DEPTH,
} from "../smartlayout/constants";
import "../smartlayout/layout.css";
import { AskLevelFragment, BidLevelFragment } from "./OrderbookLevelFragments";
import { SubscriptionClient } from "../../sdk";

interface Orderbook002Props {
  baseURL: string;
  subscriptionClient: SubscriptionClient;
  marketId: string;
  maxDepth?: number;
  height?: string;
  onPriceClick?: (price: number) => void;
}

function Orderbook002({
  baseURL,
  subscriptionClient,
  marketId,
  maxDepth = DEFAULT_MAX_DEPTH,
  height = DEFAULT_HEIGHT_ORDERBOOK,
  onPriceClick,
}: Orderbook002Props): JSX.Element {
  const { asks, bids, spread, loading, error, isConnected, wsError } = useOrderbook({
    baseURL,
    subscriptionClient,
    marketId,
    maxDepth,
  });

  // Refs for measuring and scrolling
  const orderbookSectionRef = useRef<HTMLDivElement>(null);
  const orderbookContentRef = useRef<HTMLDivElement>(null);
  const midPriceRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef<boolean>(false);
  const scrollEndTimeoutRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const isMouseInteractingRef = useRef<boolean>(false);
  const lastAnimationEndRef = useRef<number>(0);

  // Calculate max cumulative for depth visualization
  // Both asks and bids are descending (last element has highest cumulative)
  // Optimized: only depend on last elements, not entire arrays
  const lastAskCumulative =
    asks.length > 0
      ? (asks[asks.length - 1]?.cumulative ?? 0)
      : 0;
  const lastBidCumulative =
    bids.length > 0
      ? (bids[bids.length - 1]?.cumulative ?? 0)
      : 0;

  const maxCumulative = useMemo(
    () => Math.max(lastAskCumulative, lastBidCumulative),
    [lastAskCumulative, lastBidCumulative],
  );

  // Format functions are pure - no need for useCallback
  const formatPriceMemo = formatPrice;
  const formatSizeMemo = formatSize;

  const getDepthPercentage = useCallback(
    (cumulative: number): number => {
      if (maxCumulative === 0) return 0;
      return (cumulative / maxCumulative) * 100;
    },
    [maxCumulative],
  );

  // Smooth scroll animation with easing that decelerates to zero
  const smoothScrollToCenter = useCallback(
    (
      content: HTMLElement,
      targetScroll: number,
      currentScroll: number,
    ): void => {
      if (isAnimatingRef.current) {
        return; // Prevent multiple animations
      }

      isAnimatingRef.current = true;
      const startScroll = currentScroll;
      const distance = targetScroll - startScroll;
      const duration = 300; // milliseconds - shorter duration for snappier UI in dynamic orderbook
      const startTime = performance.now();
      const endTime = startTime + duration; // Pre-calculate end time for precision

      // Create cubic-bezier easing function with control points (0.57, 0.4, 0.55, 1.17)
      const easeCubicBezier = createCubicBezierEasing(0.57, 0.4, 0.55, 1.17);

      const animate = (currentTime: number) => {
        // Check if we've reached or passed the end time (accounting for frame rate)
        // This ensures we stop exactly at or before the duration, even if the frame
        // arrives slightly late due to frame rate variations
        if (currentTime >= endTime) {
          // Duration complete - set final position and stop immediately
          content.scrollTop = Math.max(0, targetScroll);
          isAnimatingRef.current = false;
          lastAnimationEndRef.current = performance.now(); // Track when animation ended
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          return;
        }

        // Calculate progress (0 to 1) based on elapsed time, clamped to never exceed 1.0
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1.0);

        // Apply cubic-bezier easing to get the eased progress value
        // This gives us the exact position on the easing curve timeline
        const eased = easeCubicBezier(progress);

        // Calculate the exact scroll position based on the eased progress
        // This ensures each frame plots the exact location according to the easing curve
        const newScroll = startScroll + distance * eased;
        content.scrollTop = Math.max(0, newScroll);

        // Continue animation - requestAnimationFrame will fire at the next frame
        // (typically ~16.67ms later at 60fps, but can vary)
        // The animation will complete when currentTime >= endTime (checked at start of function)
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    },
    [],
  );

  // Reusable function to calculate target scroll position and trigger scroll
  const scrollToCenterIfNeeded = useCallback(
    (retryCount: number = 0) => {
      // Don't run if animation is already in progress
      if (isAnimatingRef.current) {
        return;
      }

      // Debounce: Don't start new animation too soon after previous one ended
      // This prevents animation chaining from frequent WebSocket updates
      const timeSinceLastAnimation =
        performance.now() - lastAnimationEndRef.current;
      const cooldownPeriod = 500; // milliseconds - minimum time between animations
      if (timeSinceLastAnimation < cooldownPeriod) {
        return;
      }

      // orderbook-section is the scrollable element (has overflow-y: auto)
      // orderbook-content is just the container (has overflow: hidden)
      if (!orderbookSectionRef.current || !orderbookContentRef.current) {
        return;
      }

      const scrollableElement = orderbookSectionRef.current; // This is the scrollable one
      const containerElement = orderbookContentRef.current; // This is the container
      const contentHeight = containerElement.clientHeight;

      if (contentHeight === 0) {
        return;
      }

      const halfContentHeight = contentHeight / 2;
      const ASK_ELEMENT_HEIGHT = 20.83333;
      const totalAskHeight = asks.length * ASK_ELEMENT_HEIGHT;

      // Only proceed if asks height exceeds half of content height
      if (totalAskHeight <= halfContentHeight) {
        return;
      }

      // Check if spread is valid (greater than 0) and both asks and bids exist
      if (
        spread <= 0 ||
        asks.length === 0 ||
        bids.length === 0
      ) {
        return;
      }

      // Now check if spread element is rendered in DOM
      // Retry up to 3 times if element is not ready yet
      if (!midPriceRef.current) {
        if (retryCount < 3) {
          // Clear any existing retry timeout
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          const delay = 50 * (retryCount + 1);
          // Retry after a short delay
          retryTimeoutRef.current = window.setTimeout(() => {
            retryTimeoutRef.current = null;
            scrollToCenterIfNeeded(retryCount + 1);
          }, delay); // Increasing delay: 50ms, 100ms, 150ms
        }
        return;
      }

      const midPrice = midPriceRef.current;
      const midPriceTop = midPrice.offsetTop;
      const midPriceHeight = midPrice.offsetHeight;

      if (midPriceHeight === 0) {
        // Element exists but not fully rendered, retry
        if (retryCount < 3) {
          // Clear any existing retry timeout
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          const delay = 50 * (retryCount + 1);
          retryTimeoutRef.current = window.setTimeout(() => {
            retryTimeoutRef.current = null;
            scrollToCenterIfNeeded(retryCount + 1);
          }, delay);
        }
        return;
      }

      // Calculate target scroll position to center the spread element
      const spreadCenter = midPriceTop + midPriceHeight / 2;
      const contentCenter = contentHeight / 2;
      const targetScrollPosition =
        spreadCenter - contentCenter - ASK_ELEMENT_HEIGHT;

      const currentScroll = scrollableElement.scrollTop;
      const scrollDifference = Math.abs(targetScrollPosition - currentScroll);

      // Only animate if difference is significant (more than 1px)
      if (scrollDifference > 1) {
        smoothScrollToCenter(
          scrollableElement,
          Math.max(0, targetScrollPosition),
          currentScroll,
        );
      }
    },
    [asks.length, spread, smoothScrollToCenter],
  );

  // Global mouse up handler to catch mouse releases outside the element
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isMouseInteractingRef.current) {
        isMouseInteractingRef.current = false;
        // Check if we need to scroll to center after mouse release
        scrollToCenterIfNeeded();
      }
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [scrollToCenterIfNeeded]);

  // Auto-scroll to center the spread element when asks exceed half of content height
  useEffect(() => {
    if (!orderbookSectionRef.current || !orderbookContentRef.current) {
      return;
    }

    // Use double requestAnimationFrame to ensure DOM has fully rendered
    // First RAF: wait for React to commit changes
    // Second RAF: wait for browser to paint and layout
    const measureAndScroll = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToCenterIfNeeded();
      });
    });

    return () => {
      cancelAnimationFrame(measureAndScroll);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
        scrollEndTimeoutRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, [
    asks.length,
    bids.length,
    spread,
    maxCumulative,
    scrollToCenterIfNeeded,
  ]);

  // Stop auto-scroll animation when mouse interaction starts
  const stopAutoScroll = useCallback(() => {
    if (isAnimatingRef.current && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      isAnimatingRef.current = false;
    }
    // Clear any pending scroll end timeout
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
      scrollEndTimeoutRef.current = null;
    }
  }, []);

  // Handle mouse down on scrollbar
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Check if clicking on or near the scrollbar
      const element = e.currentTarget;
      const scrollbarWidth = element.offsetWidth - element.clientWidth;

      if (scrollbarWidth > 0) {
        const rect = element.getBoundingClientRect();
        const clickX = e.clientX - rect.left;

        // If click is in the scrollbar area (right side of element)
        // Add a small tolerance (5px) to catch clicks near the scrollbar
        if (clickX > element.clientWidth - scrollbarWidth - 5) {
          isMouseInteractingRef.current = true;
          stopAutoScroll();
        }
      }
    },
    [stopAutoScroll],
  );

  // Handle mouse move (drag) - stop auto-scroll if mouse is down
  const handleMouseMove = useCallback(() => {
    if (isMouseInteractingRef.current) {
      stopAutoScroll();
    }
  }, [stopAutoScroll]);

  // Handle mouse up - resume auto-scroll check
  const handleMouseUp = useCallback(() => {
    if (isMouseInteractingRef.current) {
      isMouseInteractingRef.current = false;
      // Check if we need to scroll to center after mouse release
      scrollToCenterIfNeeded();
    }
  }, [scrollToCenterIfNeeded]);

  // Monitor scroll events - cancel automatic scrolling when user manually scrolls
  // and trigger auto-scroll when scroll ends if not at target position
  const handleScroll = useCallback(() => {
    // Cancel any ongoing smooth scroll animation when user manually scrolls
    stopAutoScroll();

    // Clear existing timeout
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    // Only detect scroll end if mouse is not interacting with scrollbar
    // (mouse interaction is handled separately)
    if (!isMouseInteractingRef.current) {
      // Detect scroll end - when user releases scroll
      // Wait for scroll to stop (no scroll events for 150ms)
      scrollEndTimeoutRef.current = window.setTimeout(() => {
        scrollEndTimeoutRef.current = null;

        // Check if we need to scroll to center after manual scroll ends
        scrollToCenterIfNeeded();
      }, 150);
    }
  }, [scrollToCenterIfNeeded, stopAutoScroll]);

  if (loading) {
    return <LoadingMessage message="Loading orderbook..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* WebSocket Error Notice */}
      {wsError && (asks.length > 0 || bids.length > 0) && (
        <div className="orderbook-ws-warning">
          <img
            src="/caution-icon.svg"
            alt="Caution"
            className="orderbook-ws-warning-icon"
          />
          <span className="orderbook-ws-warning-text">{wsError}</span>
        </div>
      )}

      {/* Header */}
      <LayoutHeader title={<StatusIndicator isConnected={isConnected} />}>
        <div>Depth: {maxDepth}</div>
      </LayoutHeader>
      {/* Orderbook Content */}
      <div className="orderbook-content" ref={orderbookContentRef}>
        {/* Combined Section for Asks and Bids */}
        <div
          className="orderbook-section"
          ref={orderbookSectionRef}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Asks (Sell Orders) */}
          {asks.map((level, index) => {
            const depthPercent = getDepthPercentage(level.cumulative);
            return (
              <AskLevelFragment
                key={`ask-${level.price}-${index}`}
                level={level}
                index={index}
                isLast={
                  index === asks.length - 1 &&
                  bids.length === 0
                }
                depthPercent={depthPercent}
                formatPrice={formatPriceMemo}
                formatSize={formatSizeMemo}
                onPriceClick={onPriceClick}
              />
            );
          })}
          {spread > 0 &&
            asks.length > 0 &&
            bids.length > 0 && (
              <div className="orderbook-mid-price-spread" ref={midPriceRef}>
                Spread: {formatPriceMemo(spread)}
              </div>
            )}
          {/* Bids (Buy Orders) */}
          {bids.map((level, index) => {
            const depthPercent = getDepthPercentage(level.cumulative);
            return (
              <BidLevelFragment
                key={`bid-${level.price}-${index}`}
                level={level}
                index={index}
                isLast={index === bids.length - 1}
                depthPercent={depthPercent}
                formatPrice={formatPriceMemo}
                formatSize={formatSizeMemo}
                onPriceClick={onPriceClick}
              />
            );
          })}

          {/* Empty Messages */}
          {asks.length === 0 && bids.length === 0 && (
            <EmptyMessage message="No orders" />
          )}
          {asks.length === 0 && bids.length > 0 && (
            <EmptyMessage message="No ask orders" />
          )}
          {bids.length === 0 && asks.length > 0 && (
            <EmptyMessage message="No bid orders" />
          )}
        </div>
      </div>

      {/* Footer - Market Depth Summary */}
      <FooterSummary
        totalBids={lastBidCumulative}
        totalAsks={lastAskCumulative}
      />
    </div>
  );
}

// Memoized footer component to prevent recalculation
const FooterSummary = React.memo(
  ({ totalBids, totalAsks }: { totalBids: number; totalAsks: number }) => {
    const total = totalBids + totalAsks;
    const bidsPercent =
      total > 0 ? `${((totalBids / total) * 100).toFixed(0)}%` : "0%";
    const asksPercent =
      total > 0 ? `${((totalAsks / total) * 100).toFixed(0)}%` : "0%";

    return (
      <div className="orderbook-footer">
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar bid" />
          <span className="color-text-muted">{bidsPercent}</span>
        </div>
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar ask" />
          <span className="color-text-muted">{asksPercent}</span>
        </div>
      </div>
    );
  },
);

FooterSummary.displayName = "FooterSummary";

export default React.memo(Orderbook002);
